import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { ApiHttpError, RouteNotFoundError } from "../api/client";
import {
  listRefundCandidates,
  type RefundCandidateView,
} from "../api/refunds";

// Unresolved-deposit refund candidates list.
//
// Bound to `GET /v2/admin/bff/escrow/refund/candidates` (bearer
// session + trusted device, no HMAC in browser). Each row is a
// payment_intent whose terminal status (FAILED / EXPIRED /
// CANCELLED) carries an on-chain deposit_tx_hash — i.e. the buyer's
// funds are locked in escrow and the admin needs to drain via
// refund(invoiceId).
//
// The row's `refundToPayer` (rendered in the detail page) is the
// contract-stored payer and is READ-ONLY — admin cannot redirect
// funds.

function ResolutionBadge({ version }: { version: RefundCandidateView["escrowVersion"] }) {
  if (version === "v2_deprecated") {
    return (
      <span
        style={{
          fontSize: 11,
          padding: "2px 6px",
          borderRadius: 3,
          background: "rgba(251,191,36,0.12)",
          color: "var(--warn, #b58900)",
          border: "1px solid var(--warn, #b58900)",
        }}
      >
        V2 (deprecated)
      </span>
    );
  }
  if (version === "v3") {
    return (
      <span
        style={{
          fontSize: 11,
          padding: "2px 6px",
          borderRadius: 3,
          background: "rgba(74,222,128,0.08)",
          color: "var(--ok, #0a9444)",
          border: "1px solid var(--ok, #0a9444)",
        }}
      >
        V3
      </span>
    );
  }
  return (
    <span
      style={{
        fontSize: 11,
        padding: "2px 6px",
        borderRadius: 3,
        background: "rgba(210,63,63,0.06)",
        color: "var(--danger, #d23f3f)",
        border: "1px solid var(--danger, #d23f3f)",
      }}
    >
      unknown escrow
    </span>
  );
}

function StatusCell({ c }: { c: RefundCandidateView }) {
  return (
    <div>
      <div style={{ fontFamily: "monospace", fontSize: 12 }}>{c.status}</div>
      {c.failureReason && (
        <div style={{ fontSize: 11, color: "var(--fg-dim)", marginTop: 2 }}>
          {c.failureReason}
        </div>
      )}
    </div>
  );
}

function BillingCell({ c }: { c: RefundCandidateView }) {
  if (c.billing === null) {
    return <em style={{ fontSize: 11, color: "var(--fg-dim)" }}>none</em>;
  }
  return (
    <div>
      <div style={{ fontFamily: "monospace", fontSize: 12 }}>{c.billing.status}</div>
      {c.billing.sourceService && (
        <div
          style={{
            fontSize: 10,
            color: "var(--fg-dim)",
            marginTop: 2,
            wordBreak: "break-all",
          }}
        >
          {c.billing.sourceService}
        </div>
      )}
    </div>
  );
}

function RefundCandidatesInner() {
  const [rows, setRows] = useState<RefundCandidateView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const r = await listRefundCandidates();
      setRows(r.candidates);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <p style={{ margin: "8px 0", fontSize: 12, color: "var(--fg-dim)" }}>
        Bound to <code>GET /v2/admin/bff/escrow/refund/candidates</code> —
        bearer session + trusted device, no HMAC in browser. Each row is
        an unresolved on-chain deposit awaiting an admin-driven
        refund(invoiceId).
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
        <button onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        {rows && (
          <span style={{ fontSize: 12, color: "var(--fg-dim)" }}>
            {rows.length} candidate{rows.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {error && <ErrorBox text={error} />}
      {rows && rows.length === 0 && (
        <p style={{ padding: 16, fontSize: 13, color: "var(--fg-dim)" }}>
          No unresolved deposits. (Includes only terminal-status intents
          with a recorded deposit_tx_hash. Settled and unpaid attempts
          are excluded by construction.)
        </p>
      )}
      {rows && rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              minWidth: 1100,
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border, #ccc)", textAlign: "left" }}>
                <th style={{ padding: "8px 6px" }}>Domain</th>
                <th style={{ padding: "8px 6px" }}>paymentIntentId</th>
                <th style={{ padding: "8px 6px" }}>Status</th>
                <th style={{ padding: "8px 6px" }}>Escrow</th>
                <th style={{ padding: "8px 6px" }}>Amount</th>
                <th style={{ padding: "8px 6px" }}>Payer</th>
                <th style={{ padding: "8px 6px" }}>Deposit tx</th>
                <th style={{ padding: "8px 6px" }}>Billing</th>
                <th style={{ padding: "8px 6px" }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr
                  key={c.paymentIntentId}
                  style={{ borderBottom: "1px solid var(--border-faint, #eee)" }}
                >
                  <td style={{ padding: "8px 6px" }}>{c.domain}</td>
                  <td
                    style={{
                      padding: "8px 6px",
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                  >
                    <Link
                      to={`/escrow/refunds/${encodeURIComponent(c.paymentIntentId)}`}
                      style={{ color: "var(--accent, #2c7be5)" }}
                    >
                      {c.paymentIntentId}
                    </Link>
                  </td>
                  <td style={{ padding: "8px 6px" }}>
                    <StatusCell c={c} />
                  </td>
                  <td style={{ padding: "8px 6px" }}>
                    <ResolutionBadge version={c.escrowVersion} />
                    <div
                      style={{
                        fontSize: 10,
                        color: "var(--fg-dim)",
                        marginTop: 2,
                        wordBreak: "break-all",
                      }}
                    >
                      <code>{c.escrowContract}</code>
                    </div>
                  </td>
                  <td style={{ padding: "8px 6px" }}>
                    <div style={{ fontFamily: "monospace" }}>
                      {c.amount.display} {c.token.symbol}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--fg-dim)", marginTop: 2 }}>
                      {c.amount.baseUnits} base
                    </div>
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      fontFamily: "monospace",
                      fontSize: 11,
                      wordBreak: "break-all",
                    }}
                    title="Contract-stored payer (refund destination). Admin cannot redirect funds."
                  >
                    {c.intent.payerWallet}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      fontFamily: "monospace",
                      fontSize: 11,
                      wordBreak: "break-all",
                    }}
                  >
                    {c.depositTxHash.slice(0, 10)}…{c.depositTxHash.slice(-6)}
                  </td>
                  <td style={{ padding: "8px 6px" }}>
                    <BillingCell c={c} />
                  </td>
                  <td style={{ padding: "8px 6px", fontSize: 11 }}>
                    <code>{c.createdAt}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function RefundCandidates() {
  return (
    <div>
      <PageHeader
        title="Refunds — unresolved deposits"
        subtitle="Buyers whose terminal-failed checkout left funds locked in escrow. Drain via admin refund(invoiceId)."
        method="GET"
        path="/v2/admin/bff/escrow/refund/candidates"
      />
      <AuthRequired>
        <RefundCandidatesInner />
      </AuthRequired>
    </div>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: 12,
        border: "1px solid var(--danger)",
        color: "var(--danger)",
        borderRadius: 4,
        background: "rgba(255,100,100,0.08)",
      }}
    >
      {text}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof RouteNotFoundError) return `BE route missing: ${e.message}`;
  if (e instanceof ApiHttpError) {
    const reason =
      typeof e.body === "object" && e.body !== null && "reason" in e.body
        ? String((e.body as { reason: unknown }).reason)
        : null;
    return `HTTP ${e.status} on ${e.path}${reason ? ` (${reason})` : ""}`;
  }
  return e instanceof Error ? e.message : String(e);
}
